/**
 * Date Validation UI Controller - Global toast/modal display
 * 
 * Handles displaying date validation errors across all pages.
 * Listens for VALIDATION_FAILED events and shows appropriate UI.
 * 
 * Usage:
 *   window.eventBus.emit('VALIDATION_FAILED', { validation, context: 'picker' });
 */

class DateValidationUI {
  constructor() {
    this.toastElement = null;
    this.modalElement = null;
    this.bsToast = null;
    this.bsModal = null;
    
    this.init();
  }

  init() {
    const initStatus = {
      component: 'DateValidationUI',
      timestamp: new Date().toISOString(),
      toast: false,
      modal: false,
      eventListener: false
    };

    // Cache toast elements
    this.toastElement = document.getElementById('date-validation-toast');
    if (this.toastElement && typeof bootstrap !== 'undefined') {
      this.bsToast = new bootstrap.Toast(this.toastElement);
      initStatus.toast = true;
    }

    // Cache modal elements
    this.modalElement = document.getElementById('date-validation-modal');
    if (this.modalElement && typeof bootstrap !== 'undefined') {
      this.bsModal = new bootstrap.Modal(this.modalElement);
      initStatus.modal = true;
    }

    // Listen for validation events
    if (window.eventBus) {
      window.eventBus.on('VALIDATION_FAILED', (data) => {
        console.log('🔔 VALIDATION_FAILED event received:', data);
        const { validation, context, blocking } = data;
        
        if (blocking) {
          this.showValidationUI('modal', validation);
        } else {
          this.showValidationUI('toast', validation);
        }
      });
      initStatus.eventListener = true;
    }

    console.log('🎨 DateValidationUI Initialized:', initStatus);
  }

  /**
   * Show validation UI (toast or modal)
   * Consolidated method to handle both toast and modal display
   * 
   * @param {string} type - 'toast' or 'modal'
   * @param {object} validation - Validation result from DateValidationService
   */
  showValidationUI(type, validation) {
    const isModal = (type === 'modal');
    const component = isModal ? this.bsModal : this.bsToast;
    const componentName = isModal ? 'Modal' : 'Toast';
    
    // Check if component is available
    if (!component) {
      console.warn(`⚠️ ${componentName} not available${!isModal ? ', falling back to alert' : ''}`);
      if (!isModal) alert(validation.userMessage);
      return;
    }

    console.log(`${isModal ? '🚨' : '📢'} Showing date validation ${type}`);
    
    // Get message container
    const messageEl = document.getElementById(`date-validation-${type}-message`);
    if (!messageEl) {
      console.error(`❌ ${componentName} message element not found`);
      return;
    }

    // Populate pre-rendered content
    this.populateValidationContent(validation, messageEl);

    // Configure action buttons
    this.configureActionButtons(type, validation);

    // Show component
    component.show();
  }

  /**
   * Show non-blocking toast warning
   * @deprecated Use showValidationUI('toast', validation) instead
   * @param {object} validation - Validation result from DateValidationService
   */
  showToast(validation) {
    this.showValidationUI('toast', validation);
  }

  /**
   * Show blocking modal
   * @deprecated Use showValidationUI('modal', validation) instead
   * @param {object} validation - Validation result from DateValidationService
   */
  showModal(validation) {
    this.showValidationUI('modal', validation);
  }

  /**
   * Configure action buttons for toast or modal
   * 
   * @param {string} type - 'toast' or 'modal'
   * @param {object} validation - Validation result
   */
  configureActionButtons(type, validation) {
    const standardActions = document.getElementById(`date-validation-${type}-standard-actions`);
    const salesActions = document.getElementById(`date-validation-${type}-sales-actions`);

    // Show/hide action groups based on validation result
    if (validation.requiresCallSales) {
      if (standardActions) standardActions.style.display = 'none';
      if (salesActions) salesActions.style.display = 'flex';
    } else {
      if (standardActions) standardActions.style.display = 'flex';
      if (salesActions) salesActions.style.display = 'none';
      
      // Set up edit dates button
      this.setupEditDatesButton(type, validation);
    }
  }

  /**
   * Set up the edit dates button event listener
   * 
   * @param {string} type - 'toast' or 'modal'
   * @param {object} validation - Validation result
   */
  setupEditDatesButton(type, validation) {
    const editDatesBtn = document.getElementById(`edit-dates-from-${type}`);
    if (!editDatesBtn) return;
    
    // Clone to remove old event listeners
    const newEditBtn = editDatesBtn.cloneNode(true);
    editDatesBtn.parentNode.replaceChild(newEditBtn, editDatesBtn);
    
    const isModal = (type === 'modal');
    const component = isModal ? this.bsModal : this.bsToast;
    
    newEditBtn.addEventListener('click', () => {
      // Hide the current UI
      component.hide();
      
      // Emit event for date editor to open - trust the event system!
      if (window.eventBus) {
        window.eventBus.emit('OPEN_DATE_EDITOR', { 
          suggestedFix: validation.suggestedFix 
        });
      }
    });
  }

  /**
   * Populate pre-rendered validation content with dynamic values
   * @param {object} validation - Validation result
   * @param {HTMLElement} messageEl - Message container
   */
  populateValidationContent(validation, messageEl) {
    // Hide all content blocks
    const allContent = messageEl.querySelectorAll('.rr-date-validation-content');
    allContent.forEach(el => el.style.display = 'none');
    
    // Determine scenario
    let scenario = 'simple';
    if (validation.errors.length > 1) {
      scenario = 'multiple-issues';
    } else if (validation.details?.issues?.includes('duration_too_long')) {
      scenario = 'extended-rental';
    }
    
    // Show appropriate content block
    const contentBlock = messageEl.querySelector(`[data-scenario="${scenario}"]`);
    if (contentBlock) {
      contentBlock.style.display = 'block';
      
      if (scenario === 'extended-rental') {
        this.updateDynamicSpan(contentBlock, 'duration', validation.details?.duration || 0);
        this.updateDynamicSpan(contentBlock, 'max_days', validation.details?.maxDays || 14);
      } else if (scenario === 'multiple-issues') {
        // Show/hide issue items
        const issueItems = contentBlock.querySelectorAll('[data-issue]');
        issueItems.forEach(item => {
          const issueType = item.dataset.issue;
          if (validation.details?.issues?.includes(issueType)) {
            item.style.display = 'list-item';
          } else {
            item.style.display = 'none';
          }
        });
        
        this.updateDynamicSpan(
          contentBlock,
          'min_lead_days',
          validation.details?.requiredLeadTimeDays ||
            window.DateValidationService?.BUSINESS_RULES?.MIN_LEAD_TIME_DAYS ||
            2
        );
        this.updateDynamicSpan(contentBlock, 'max_days', validation.details?.maxDays || 14);
      } else if (scenario === 'simple') {
        this.updateDynamicSpan(contentBlock, 'message', validation.userMessage);
      }
    }
    
    // Handle suggested dates
    const suggestionEl = messageEl.querySelector('[data-field="suggested-dates"]');
    if (suggestionEl) {
      if (validation.suggestedFix && !validation.requiresCallSales) {
        suggestionEl.style.display = 'block';
        const suggested = validation.suggestedFix;
        const datesText = `${window.DateValidationService.formatDateFriendly(suggested.startDate)} → ${window.DateValidationService.formatDateFriendly(suggested.endDate)} (${suggested.duration} day${suggested.duration !== 1 ? 's' : ''})`;
        this.updateDynamicSpan(suggestionEl, 'suggested_dates', datesText);
      } else {
        suggestionEl.style.display = 'none';
      }
    }

    // Handle rush-rental call CTA — show whenever the start date is too soon so an
    // urgent customer can call to confirm availability instead of abandoning the order
    const rushEl = messageEl.querySelector('[data-field="rush-call"]');
    if (rushEl) {
      const isTooSoon = validation.details?.issues?.includes('insufficient_lead_time');
      rushEl.style.display = isTooSoon ? 'flex' : 'none';
    }
  }

  /**
   * Update dynamic span text content
   * @param {HTMLElement} container - Container element
   * @param {string} field - Data-field value
   * @param {string|number} value - Value to set
   */
  updateDynamicSpan(container, field, value) {
    const span = container.querySelector(`[data-field="${field}"]`);
    if (span) {
      span.textContent = value;
    }
  }
}

// Initialize globally
if (typeof window !== 'undefined') {
  window.DateValidationUI = new DateValidationUI();
}
